import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const Setup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [setupStage, setSetupStage] = useState<'form' | 'creating' | 'collecting' | 'analyzing' | 'complete'>('form');
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user is already authenticated and redirect to app
  useEffect(() => {
    const checkAuthAndAdmin = async () => {
      try {
        // First check if user is already logged in
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          // User is logged in, redirect to app
          toast({
            title: "Already Logged In",
            description: "Redirecting to your dashboard...",
          });
          setTimeout(() => navigate('/'), 1000);
          return;
        }

        // If not logged in, check if admin exists
        const { data, error } = await supabase
          .from('user_roles')
          .select('id')
          .eq('role', 'admin')
          .limit(1);

        if (!error && data && data.length > 0) {
          toast({
            title: "Setup Already Complete",
            description: "An admin user already exists. Redirecting to login...",
          });
          setTimeout(() => navigate('/admin-access'), 2000);
        } else {
          setCheckingAdmin(false);
        }
      } catch (error) {
        console.error("Error checking auth/admin:", error);
        setCheckingAdmin(false);
      }
    };

    checkAuthAndAdmin();
  }, [navigate, toast]);

  const runSetup = async () => {
    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please ensure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    // Strong password validation for admin account
    if (password.length < 12) {
      toast({
        title: "Password too short",
        description: "Password must be at least 12 characters long.",
        variant: "destructive",
      });
      return;
    }

    if (!/[A-Z]/.test(password)) {
      toast({
        title: "Weak password",
        description: "Password must contain at least one uppercase letter.",
        variant: "destructive",
      });
      return;
    }

    if (!/[a-z]/.test(password)) {
      toast({
        title: "Weak password",
        description: "Password must contain at least one lowercase letter.",
        variant: "destructive",
      });
      return;
    }

    if (!/[0-9]/.test(password)) {
      toast({
        title: "Weak password",
        description: "Password must contain at least one number.",
        variant: "destructive",
      });
      return;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      toast({
        title: "Weak password",
        description: "Password must contain at least one special character.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Create admin user via setup function
      setSetupStage('creating');
      console.log('Creating admin user...');
      
      const { data: setupData, error: setupError } = await supabase.functions.invoke('setup-admin', {
        body: { email, password },
      });

      if (setupError) {
        console.error('Setup function error:', setupError);
        throw new Error(setupError.message || 'Failed to create admin user');
      }

      if (!setupData.success) {
        throw new Error(setupData.error || 'Setup failed');
      }

      console.log('Admin user created:', setupData.userId);

      // If we got a session, set it
      if (setupData.session) {
        await supabase.auth.setSession(setupData.session);
      } else {
        // Otherwise, sign in manually
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) {
          console.error('Sign in error:', signInError);
          throw new Error('Admin created but login failed. Please log in manually at /auth');
        }
      }

      // Step 2: Collect crypto data
      setSetupStage('collecting');
      console.log('Starting data collection...');
      
      const { error: collectError } = await supabase.functions.invoke('collect-crypto-data', {
        body: {},
      });

      if (collectError) {
        console.error('Data collection error:', collectError);
        toast({
          title: "Warning",
          description: "Admin created but data collection failed. You can run it manually from the blog generator.",
          variant: "destructive",
        });
      } else {
        console.log('Data collection completed');
      }

      // Step 3: Run AI analysis
      setSetupStage('analyzing');
      console.log('Starting AI analysis...');
      
      const { error: analysisError } = await supabase.functions.invoke('ai-analyze-market', {
        body: {},
      });

      if (analysisError) {
        console.error('AI analysis error:', analysisError);
        toast({
          title: "Warning",
          description: "Data collected but AI analysis failed. You can run it manually from the blog generator.",
          variant: "destructive",
        });
      } else {
        console.log('AI analysis completed');
      }

      // Step 4: Complete
      setSetupStage('complete');
      
      toast({
        title: "Setup Complete! 🎉",
        description: "Admin account created and system initialized. Redirecting to blog generator...",
      });

      setTimeout(() => {
        navigate('/blog-generator');
      }, 2000);

    } catch (error: any) {
      console.error('Setup error:', error);
      toast({
        title: "Setup Failed",
        description: error.message || "An error occurred during setup. Please try again.",
        variant: "destructive",
      });
      setSetupStage('form');
    } finally {
      setIsLoading(false);
    }
  };

  const getStageMessage = () => {
    switch (setupStage) {
      case 'creating':
        return { icon: Loader2, text: 'Creating admin account...', spinning: true };
      case 'collecting':
        return { icon: Loader2, text: 'Collecting crypto market data...', spinning: true };
      case 'analyzing':
        return { icon: Loader2, text: 'Running AI analysis...', spinning: true };
      case 'complete':
        return { icon: CheckCircle, text: 'Setup complete! Redirecting...', spinning: false };
      default:
        return null;
    }
  };

  const stageInfo = getStageMessage();

  if (checkingAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">Checking setup status...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary to-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold mb-2">🚀 Initial Setup</CardTitle>
          <CardDescription>
            Create your admin account and initialize the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {setupStage === 'form' ? (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Admin Email</label>
                  <Input
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <Input
                    type="password"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Confirm Password</label>
                  <Input
                    type="password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  What happens next:
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>✓ Creates admin account with provided credentials</li>
                  <li>✓ Assigns admin role for full system access</li>
                  <li>✓ Collects initial crypto market data</li>
                  <li>✓ Runs AI analysis on collected data</li>
                  <li>✓ Redirects to blog generator dashboard</li>
                </ul>
              </div>

              <Button
                onClick={runSetup}
                disabled={isLoading || !email || !password || !confirmPassword}
                className="w-full"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  'Initialize System'
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                This page will only work for the first-time setup.
                <br />
                Save your credentials securely!
              </p>
            </>
          ) : stageInfo ? (
            <div className="py-12 space-y-6">
              <div className="flex justify-center">
                <stageInfo.icon 
                  className={`w-16 h-16 ${setupStage === 'complete' ? 'text-green-600' : 'text-primary'} ${stageInfo.spinning ? 'animate-spin' : ''}`} 
                />
              </div>
              <p className="text-center font-medium text-lg">{stageInfo.text}</p>
              {setupStage !== 'complete' && (
                <div className="space-y-2 text-sm text-muted-foreground text-center">
                  <p>Please wait, this may take up to 60 seconds...</p>
                  <p className="text-xs">Do not close this page</p>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default Setup;
